/**
 * SignatureAdvanceDialog — 1:1 port of the legacy post-provider-sign
 * "Note Signed Successfully!" dialog (App.js ~line 24303) for V2 hosts.
 * Keeps the exact option-visibility rules: Keep as Draft (always),
 * Send to Client (client signature required), Send to Supervisor
 * (required/optional), Mark as Complete (non-billable or CPT blocked),
 * Mark Ready for Billing (billable + unrestricted + no routing holds),
 * Mark Non-Billable (no client-signature pending), plus the CPT
 * restricted/missing warning banners.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Send, UserCheck, CheckCircle, DollarSign, Ban } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const SignatureAdvanceDialog = ({
  open,
  onOpenChange,
  note,                       // signed note driving the options
  clearNote,                  // () => setSignedNoteForAdvancement(null)
  user,
  clientsForLookup = [],
  confirmAction,              // async (message, opts) => boolean
  updateNoteStatusOptimistically,
  refreshNotes,
  refreshTodos,
  setSelectedNote,
  setShowNoteDetail,
  setShowSendToClientDialog,
  setNotBillableTargetNote,
  setNotBillableReason,
  setNotBillableComment,
  setShowNotBillableDialog,
}) => {
  // CPT × insurance restriction + missing-CPT detection (legacy parity).
  const [advanceCptBlocked, setAdvanceCptBlocked] = useState(false);
  const [advanceCptMissing, setAdvanceCptMissing] = useState(false);
  useEffect(() => {
    if (!open || !note?.id) {
      setAdvanceCptBlocked(false);
      setAdvanceCptMissing(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cpt = note.cpt_code || note._billing?.cpt_code || note.cpt_code_id;
        if (!cpt || !note.client_id) {
          if (!cancelled) setAdvanceCptBlocked(false);
          if (note.billable && !cpt) {
            let apptHasCpt = false;
            if (note.appointment_id) {
              try {
                const apptResp = await axios.get(`${API}/appointments/${note.appointment_id}`);
                apptHasCpt = !!(apptResp?.data?.cpt_code_id || apptResp?.data?.cpt_code);
              } catch { /* treat as no CPT */ }
            }
            if (!cancelled) setAdvanceCptMissing(!apptHasCpt);
          } else if (!cancelled) {
            setAdvanceCptMissing(false);
          }
          return;
        }
        if (!cancelled) setAdvanceCptMissing(false);
        const resp = await axios.post(`${API}/validate-insurance-cpt`, {
          client_id: note.client_id,
          cpt_code: cpt,
        });
        if (!cancelled) setAdvanceCptBlocked(!!resp?.data?.primary_blocked);
      } catch {
        if (!cancelled) setAdvanceCptBlocked(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, note?.id, note?.cpt_code, note?.client_id]);

  const client = clientsForLookup.find(c => c.id === note?.client_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md z-[100]" overlayClassName="z-[99]" data-testid="signature-advance-dialog">
        <DialogHeader>
          <DialogTitle>✅ Note Signed Successfully!</DialogTitle>
          <DialogDescription>
            What would you like to do next with this note?
          </DialogDescription>
        </DialogHeader>

        {note && (
          <div className="space-y-3">
            {/* Client Info */}
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm font-semibold">
                {client?.first_name} {client?.last_name}
              </p>
              <p className="text-xs text-gray-600">{note.title}</p>
            </div>

            {advanceCptBlocked && (
              <div
                data-testid="provider-advance-cpt-blocked-banner"
                className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
              >
                <strong>CPT restricted:</strong> This note's CPT code is not
                billable to the client's primary insurance.  Mark Complete
                or Mark Non-Billable instead — sending to billing will be
                rejected.
              </div>
            )}

            {advanceCptMissing && (
              <div
                data-testid="provider-advance-cpt-missing-banner"
                className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
              >
                <strong>Missing CPT code:</strong> This note can't go to
                billing until a CPT code is added. Choose <em>Keep as
                Draft</em>, add the CPT code to the note (or its linked
                appointment), then sign and advance it. Sending it to
                your supervisor will <em>not</em> add the CPT.
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              {/* Keep as Draft */}
              <Button
                variant="outline"
                className="w-full justify-start"
                data-testid="advance-keep-as-draft"
                onClick={() => {
                  onOpenChange(false);
                  clearNote();
                  setShowNoteDetail(false);
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Keep as Draft
                <span className="ml-auto text-xs text-gray-500">Review later</span>
              </Button>

              {/* Send to Client (if requires client signature) */}
              {(note.require_client_signature || note.requires_client_signature) && (
                <Button
                  className="w-full justify-start bg-blue-600 hover:bg-blue-700"
                  data-testid="advance-send-to-client"
                  onClick={() => {
                    setSelectedNote(note);
                    onOpenChange(false);
                    clearNote();
                    setShowSendToClientDialog(true);
                  }}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send to Client for Signature
                  <span className="ml-auto text-xs">Required</span>
                </Button>
              )}

              {/* Send to Supervisor */}
              {(note.requires_supervisor_signature ||
                note.assigned_supervisor ||
                user?.supervisor_id ||
                (user?.supervisor_ids && user?.supervisor_ids.length > 0)) && (
                <Button
                  className={`w-full justify-start ${note.requires_supervisor_signature ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'}`}
                  data-testid="advance-send-to-supervisor"
                  onClick={async () => {
                    const noteId = note.id;
                    if (advanceCptMissing && !note.requires_supervisor_signature) {
                      const ok = await confirmAction(
                        "This note has no CPT code, so it can't be billed even after your supervisor signs. Add the CPT code to the note instead (Keep as Draft → edit → sign). Send to supervisor anyway?",
                        { title: 'Missing CPT code', confirmLabel: 'Send anyway', cancelLabel: 'Go back' }
                      );
                      if (!ok) return;
                    }
                    updateNoteStatusOptimistically(noteId, 'sent_to_supervisor');
                    try {
                      await axios.put(`${API}/notes/${noteId}`, { status: 'sent_to_supervisor' });
                      toast.success('Note sent to supervisor for review');
                      onOpenChange(false);
                      clearNote();
                      setShowNoteDetail(false);
                      refreshNotes(); refreshTodos();
                    } catch (error) {
                      toast.error('Failed to send to supervisor');
                      refreshNotes(); refreshTodos();
                    }
                  }}
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Send to Supervisor
                  <span className="ml-auto text-xs">{note.requires_supervisor_signature ? 'Required' : 'Optional'}</span>
                </Button>
              )}

              {/* Mark as Complete (non-billable OR CPT restricted) */}
              {(!note.billable || advanceCptBlocked) &&
               !note.require_client_signature &&
               !note.requires_client_signature && (
                <Button
                  className="w-full justify-start bg-green-600 hover:bg-green-700"
                  data-testid="provider-advance-mark-complete"
                  onClick={async () => {
                    const noteId = note.id;
                    updateNoteStatusOptimistically(noteId, 'completed');
                    try {
                      await axios.put(`${API}/notes/${noteId}`, { status: 'completed' });
                      toast.success('Note marked as complete');
                      onOpenChange(false);
                      clearNote();
                      refreshNotes(); refreshTodos();
                    } catch (error) {
                      toast.error('Failed to mark as complete');
                      refreshNotes(); refreshTodos();
                    }
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Complete
                  <span className="ml-auto text-xs text-gray-200">{advanceCptBlocked ? 'CPT restricted' : 'Non-billable'}</span>
                </Button>
              )}

              {/* Ready for Billing */}
              {note.billable &&
               !advanceCptBlocked &&
               !note.requires_supervisor_signature &&
               !note.require_client_signature &&
               !note.requires_client_signature && (
                <Button
                  className="w-full justify-start bg-teal-600 hover:bg-teal-700"
                  data-testid="advance-ready-for-billing"
                  onClick={async () => {
                    const noteId = note.id;
                    updateNoteStatusOptimistically(noteId, 'signed_ready_for_billing');
                    try {
                      const resp = await axios.put(`${API}/notes/${noteId}`, { status: 'signed_ready_for_billing' });
                      const newStatus = resp?.data?.status;
                      if (newStatus === 'pending_supervisor_signature') {
                        toast.success('Routed to supervisor for signature', {
                          description: 'The supervisor was notified and the note will auto-advance to ready-for-billing once they sign.',
                        });
                      } else {
                        toast.success('Note ready for billing');
                      }
                      onOpenChange(false);
                      clearNote();
                      setShowNoteDetail(false);
                      refreshNotes(); refreshTodos();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to mark ready for billing');
                      onOpenChange(false);
                      clearNote();
                      refreshNotes(); refreshTodos();
                    }
                  }}
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Mark Ready for Billing
                  <span className="ml-auto text-xs text-gray-200">Billable</span>
                </Button>
              )}

              {/* Mark Non-Billable escape hatch */}
              {!note.require_client_signature &&
               !note.requires_client_signature && (
                <Button
                  variant="outline"
                  className="w-full justify-start border-slate-500 text-slate-700 hover:bg-slate-50"
                  data-testid="provider-advance-mark-not-billable"
                  onClick={() => {
                    setNotBillableTargetNote(note);
                    setNotBillableReason(advanceCptBlocked ? 'cpt_restricted_for_insurance' : '');
                    setNotBillableComment('');
                    onOpenChange(false);
                    clearNote();
                    setShowNotBillableDialog(true);
                  }}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Mark Non-Billable
                  <span className="ml-auto text-xs text-gray-500">Duplicate / Restricted</span>
                </Button>
              )}
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              You can change the status later from the note details
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
